import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, X, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Droppable, pointerFirstCollisionDetection } from '../../components/ui/sortable';
import { dataApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { isPdfFile } from '../../lib/photoGroups';

/**
 * ImageUploadDialog — staging popup for adding photos to a quote request.
 *
 * Two areas (Damage / Other). Drop OS files into either, drag the staged
 * thumbnails between the areas or reorder within, then upload. Damage photos
 * feed the AI assessment; other photos are reference only.
 *
 * A PDF can be attached too, as a reference document: it always goes to Other
 * and cannot be dragged into Damage, because the AI cannot look at it.
 *
 * `initialFiles` stages files dropped on the page rather than into an area —
 * the page opens this dialog with them under Other, to be sorted from here.
 * Each new array is staged once, so a further drop while open adds to them.
 */

const AREAS = [
  { key: 'damage', title: 'Damage photos', category: 'damage' },
  { key: 'other', title: 'Other photos', category: 'extra' },
];

let stagedSeq = 0;
const nextStagedId = () => `staged-${Date.now()}-${stagedSeq++}`;

// Presentational thumbnail — reused for both the sortable tile and the drag overlay
const ThumbVisual = React.forwardRef(function ThumbVisual(
  { item, onRemove, overlay = false, dragging = false, style, className, ...handlers },
  ref
) {
  return (
    <div
      ref={ref}
      style={style}
      {...handlers}
      className={cn(
        'group/thumb relative aspect-square rounded-md overflow-hidden bg-muted touch-none',
        overlay ? 'shadow-lg ring-1 ring-black/10 cursor-grabbing' : 'cursor-grab active:cursor-grabbing',
        dragging && 'opacity-40',
        className
      )}
    >
      {isPdfFile(item.file) ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-1.5 text-muted-foreground">
          <FileText className="h-6 w-6" />
          <span className="w-full text-center text-[10px] leading-tight line-clamp-2 break-all">{item.name}</span>
        </div>
      ) : (
        <img src={item.preview} alt={item.name} className="w-full h-full object-cover" draggable={false} />
      )}
      {!overlay && (
        <button
          type="button"
          title="Remove"
          onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-1 right-1 h-6 w-6 rounded-md bg-background/80 text-muted-foreground hover:text-destructive hover:bg-background flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 focus-visible:opacity-100 transition-opacity"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
});

function StagedThumb({ item, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <ThumbVisual
      ref={setNodeRef}
      item={item}
      onRemove={onRemove}
      dragging={isDragging}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
    />
  );
}

function UploadArea({ area, items, onAddFiles, onRemove }) {
  const inputRef = useRef(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const ids = items.map((it) => it.id);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{area.title}</p>
        {items.length > 0 && <span className="text-xs text-muted-foreground">{items.length}</span>}
      </div>

      <Droppable id={`area:${area.key}`} data={{ area: area.key }}>
        <div
          onDragOver={(e) => { e.preventDefault(); setIsFileDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsFileDragging(false); }}
          onDrop={(e) => {
            // Native OS file drop (dnd-kit thumbnail drags carry no files)
            if (e.dataTransfer?.files?.length) {
              e.preventDefault();
              setIsFileDragging(false);
              onAddFiles(area.key, e.dataTransfer.files);
            }
          }}
          className={cn(
            'rounded-lg border-2 border-dashed p-3 min-h-[6rem] transition-colors',
            isFileDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          )}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {items.map((item) => (
                <StagedThumb key={item.id} item={item} onRemove={onRemove} />
              ))}
              <button
                type="button"
                aria-label={`Add ${area.title}`}
                onClick={() => inputRef.current?.click()}
                className="aspect-square rounded-md border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
              >
                <Plus className="h-6 w-6" />
              </button>
            </div>
          </SortableContext>
        </div>
      </Droppable>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          onAddFiles(area.key, e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

const ImageUploadDialog = ({ isOpen, onClose, quoteRequestId, startOrder = 0, onUploaded, initialFiles = null }) => {
  const [staged, setStaged] = useState({ damage: [], other: [] });
  const [activeId, setActiveId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const totalStaged = staged.damage.length + staged.other.length;

  const findArea = useCallback((id) => {
    if (id === 'area:damage' || staged.damage.some((it) => it.id === id)) return 'damage';
    if (id === 'area:other' || staged.other.some((it) => it.id === id)) return 'other';
    return null;
  }, [staged]);

  const addFiles = useCallback((areaKey, fileList) => {
    const offered = Array.from(fileList || []);
    const files = offered.filter((f) => f.type?.startsWith('image/') || isPdfFile(f));
    if (files.length < offered.length) {
      toast.warning('Only photos and PDFs can be added');
    }
    if (files.length === 0) return;
    const toItem = (file) => ({
      id: nextStagedId(),
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
    });
    const pdfs = files.filter((file) => isPdfFile(file)).map(toItem);
    const photos = files.filter((file) => !isPdfFile(file)).map(toItem);
    setStaged((prev) => {
      const next = { ...prev, [areaKey]: [...prev[areaKey], ...photos] };
      next.other = [...next.other, ...pdfs];
      return next;
    });
  }, []);

  // Files dropped on the page. The ref makes staging idempotent per array: an
  // effect that runs twice for the same drop must not stage it twice.
  const stagedInitialRef = useRef(null);
  useEffect(() => {
    if (!initialFiles || stagedInitialRef.current === initialFiles) return;
    stagedInitialRef.current = initialFiles;
    addFiles('other', initialFiles);
  }, [initialFiles, addFiles]);

  const removeItem = (id) => {
    setStaged((prev) => {
      const next = { damage: [...prev.damage], other: [...prev.other] };
      for (const key of ['damage', 'other']) {
        const idx = next[key].findIndex((it) => it.id === id);
        if (idx >= 0) {
          URL.revokeObjectURL(next[key][idx].preview);
          next[key].splice(idx, 1);
          break;
        }
      }
      return next;
    });
  };

  // Live relocation across areas while dragging (matches DamagePartsList pattern)
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const fromArea = findArea(active.id);
    const toArea = findArea(over.id);
    if (!fromArea || !toArea || fromArea === toArea) return;
    // A PDF is reference only — the AI cannot look at it.
    if (toArea === 'damage' && staged[fromArea].some((it) => it.id === active.id && isPdfFile(it.file))) return;

    setStaged((prev) => {
      const fromList = [...prev[fromArea]];
      const toList = [...prev[toArea]];
      const movingIndex = fromList.findIndex((it) => it.id === active.id);
      if (movingIndex < 0) return prev;
      const [moving] = fromList.splice(movingIndex, 1);

      const overIndex = toList.findIndex((it) => it.id === over.id);
      const insertAt = overIndex >= 0 ? overIndex : toList.length;
      toList.splice(insertAt, 0, moving);

      return { ...prev, [fromArea]: fromList, [toArea]: toList };
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const area = findArea(active.id);
    if (!area) return;
    if (findArea(over.id) === area && active.id !== over.id) {
      setStaged((prev) => {
        const list = prev[area];
        const oldIndex = list.findIndex((it) => it.id === active.id);
        const newIndex = list.findIndex((it) => it.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return { ...prev, [area]: arrayMove(list, oldIndex, newIndex) };
      });
    }
  };

  const cleanupPreviews = () => {
    [...staged.damage, ...staged.other].forEach((it) => URL.revokeObjectURL(it.preview));
  };

  const handleClose = () => {
    if (uploading) return;
    cleanupPreviews();
    setStaged({ damage: [], other: [] });
    onClose();
  };

  const handleUpload = async () => {
    if (totalStaged === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: totalStaged });

    const uploads = [
      ...staged.damage.map((it) => ({ it, category: 'damage' })),
      ...staged.other.map((it) => ({ it, category: 'extra' })),
    ];

    let done = 0;
    let failed = 0;
    for (const [index, { it, category }] of uploads.entries()) {
      try {
        const record = await dataApi.uploadQuoteImage(quoteRequestId, it.file, {
          category,
          name: it.name,
          order: startOrder + index,
        });
        onUploaded?.({ ...record, id: record.$id, url: dataApi.getImageUrl(record.storagePath) });
      } catch (err) {
        console.error('Photo upload failed:', it.name, err);
        failed += 1;
      } finally {
        done += 1;
        setProgress({ done, total: totalStaged });
      }
    }

    setUploading(false);
    setProgress(null);
    if (failed > 0) {
      toast.warning(`${failed} photo${failed === 1 ? '' : 's'} failed to upload`);
    } else {
      toast.success(`${uploads.length} photo${uploads.length === 1 ? '' : 's'} added`);
    }
    cleanupPreviews();
    setStaged({ damage: [], other: [] });
    onClose();
  };

  const activeItem = activeId
    ? [...staged.damage, ...staged.other].find((it) => it.id === activeId)
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add photos</DialogTitle>
        </DialogHeader>

        <DndContext
          sensors={sensors}
          collisionDetection={pointerFirstCollisionDetection}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragCancel={() => setActiveId(null)}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-4">
            {AREAS.map((area) => (
              <UploadArea
                key={area.key}
                area={area}
                items={staged[area.key]}
                onAddFiles={addFiles}
                onRemove={removeItem}
              />
            ))}
          </div>

          {/* Portal the overlay to <body> so the Dialog's centering transform
              doesn't offset the dragged thumbnail from the cursor */}
          {createPortal(
            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
              {activeItem ? (
                <div className="w-20">
                  <ThumbVisual item={activeItem} overlay />
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading || totalStaged === 0}>
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading {progress?.done}/{progress?.total}…
              </>
            ) : (
              `Upload${totalStaged ? ` ${totalStaged}` : ''}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageUploadDialog;
